import { useState } from "react";
import { Copy, Check, ThumbsUp, ThumbsDown, Bookmark, Heart, ShieldQuestion } from "lucide-react";
import { toast } from "sonner";

const CONCERN = {
  green: { label: "Friendly & harmless", cls: "pill-green" },
  amber: { label: "Worth a gentle chat", cls: "pill-amber" },
  red: { label: "A caring check-in helps", cls: "pill-red" },
};

export default function ResultCard({ result, onSave, onFeedback, saved, feedback }) {
  const [copied, setCopied] = useState(false);
  if (!result) return null;
  const concern = CONCERN[result.concern_level] || CONCERN.green;

  const copyReply = async () => {
    try {
      await navigator.clipboard.writeText(result.suggested_reply || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success("Reply copied");
    } catch {
      toast.error("Press Ctrl+C or Cmd+C to copy");
    }
  };

  return (
    <div className="dk-card p-6 space-y-5 anim-in" data-testid="result-card">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <span className="badge-soft" data-testid="result-card-category">{result.category}</span>
          <h3 className="font-serif text-[22px] font-semibold" data-testid="result-card-slang-title">{result.term}</h3>
        </div>
        <span className={`badge-soft ${concern.cls}`} data-testid="result-card-concern">{concern.label}</span>
      </div>

      {result.safety_notice && (
        <div className="rounded-xl p-4 flex gap-3" style={{ background: "var(--sage-tint)" }} data-testid="result-card-safety">
          <ShieldQuestion size={20} style={{ color: "var(--sage)" }} className="shrink-0 mt-0.5" />
          <p className="text-sm" style={{ color: "var(--text)" }}>{result.safety_notice}</p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="What it means" value={result.meaning} />
        <Field label="Why kids use it" value={result.context} />
        <Field label="The feeling behind it" value={result.intent} />
        <Field label="What we notice" value={result.concern_note} />
      </div>

      <div className="rounded-xl p-4" style={{ background: "var(--card-alt)" }}>
        <div className="flex items-center gap-2 mb-1">
          <Heart size={15} style={{ color: "var(--accent)" }} />
          <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Something you could say</span>
        </div>
        <p className="text-[16px]" style={{ color: "var(--text)" }} data-testid="result-card-reply">“{result.suggested_reply}”</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button className="btn btn-outline" onClick={copyReply} data-testid="result-card-copy-reply-btn">
          {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied" : "Copy reply"}
        </button>
        {onSave && (
          <button className={`btn ${saved ? "btn-secondary" : "btn-outline"}`} onClick={onSave} data-testid="result-card-save-btn">
            <Bookmark size={16} /> {saved ? "Saved" : "Save to deck"}
          </button>
        )}
        {onFeedback && (
          <div className="flex items-center gap-1 ml-auto">
            <button className={`btn btn-ghost px-3 ${feedback === true ? "text-[#2F6B4A]" : ""}`} onClick={() => onFeedback(true)} data-testid="result-card-helpful-btn" aria-label="Helpful"><ThumbsUp size={16} /></button>
            <button className={`btn btn-ghost px-3 ${feedback === false ? "text-[#A6322A]" : ""}`} onClick={() => onFeedback(false)} data-testid="result-card-unhelpful-btn" aria-label="Not helpful"><ThumbsDown size={16} /></button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>
      <p className="text-[15px]" style={{ color: "var(--text)" }}>{value}</p>
    </div>
  );
}
